import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useGenerateSimilarSubmissionsMutation } from '../../../../store/apis/submission.api'
import { Alert, Button, Form, InputNumber } from 'antd'
import { toggleGenerateSimilarSubmissionsModal } from '../../../../store/slices/assignment.slice'
import { openNotificationWithIcon } from '../../../../utils/notification.helper'
import { useParams } from 'react-router'
import patternSelectors from '../../../../store/selectors/pattern.selectors'
import { MINIMAL_NUMBER_OF_EXAMPLES_FOR_SAVE_AS_LBE } from '../../../../constants/general'
import submissionSelectors from '../../../../store/selectors/submission.selectors'
import { useFetchEstimatedSimilarSubmissionsGenerationCostQuery } from '../../../../store/apis/semantic-language.api'
import { actions } from '../../../../store/reducers/assignment.reducer'
import { useAutoFocusFirstInput } from '../../../../../hooks/useAutoFocusFirstInput'

const GenerateSimilarSubmissionsForm = () => {
    const openedSubmissionId = useSelector(submissionSelectors.openedSubmissionId)
    const editingPatternId = useSelector(patternSelectors.editingPatternId)
    const numberOfSubmissionsHitByEditingPattern = useSelector(patternSelectors.numberOfSubmissionsHitByEditingPattern)

    const dispatch = useDispatch()

    const { token, layerId } = useParams()

    const [form] = Form.useForm()
    useAutoFocusFirstInput()
    const numberOfSubmissionsToGenerate = Form.useWatch('numberOfSubmissionsToGenerate', form)
    const [generateSimilarSubmissions, generateSimilarSubmissionsResult] = useGenerateSimilarSubmissionsMutation()

    const { data: similarSubmissionsGenerationCost, error: errorEstimatingGenerateSimilarSubmissionsCost } =
        useFetchEstimatedSimilarSubmissionsGenerationCostQuery(
            {
                token,
                submissionId: openedSubmissionId,
                numberOfSubmissionsToGenerate,
                layerId,
                patternId: editingPatternId,
            },
            { skip: !numberOfSubmissionsToGenerate },
        )

    const handleGenerate = async values => {
        const generateSimilarSubmissionsResponse = await generateSimilarSubmissions({
            token,
            layerId,
            patternId: editingPatternId,
            submissionId: openedSubmissionId,
            numberOfSubmissionsToGenerate: values.numberOfSubmissionsToGenerate,
        })
        if (generateSimilarSubmissionsResponse?.data) {
            dispatch(toggleGenerateSimilarSubmissionsModal())
            dispatch(actions.setShowEditPatternModal(false))
            dispatch(actions.setEditingPattern(null))
            dispatch(actions.fetchAssignmentInfo(token))
            openNotificationWithIcon('success', 'Generating similar submissions has been started successfully')
        }
    }

    const numberOfInsufficientSubmissions =
        MINIMAL_NUMBER_OF_EXAMPLES_FOR_SAVE_AS_LBE - numberOfSubmissionsHitByEditingPattern

    return (
        <Form
            name='generate-similar-submissions'
            onFinish={handleGenerate}
            form={form}
            autoComplete='off'
            layout='vertical'
            requiredMark={false}
            validateTrigger={['onBlur', 'onSubmit']}
        >
            <Form.Item
                name='numberOfSubmissionsToGenerate'
                label='Number of Submissions to Be Generated'
                initialValue={numberOfInsufficientSubmissions > 0 ? numberOfInsufficientSubmissions : 10}
                rules={[
                    {
                        type: 'number',
                        min: numberOfInsufficientSubmissions,
                        message: `You need to generate at least ${numberOfInsufficientSubmissions} more examples to satisfy the minimal recommended number of examples for Save as LBE.`,
                    },
                ]}
                extra={`Currently there are only ${numberOfSubmissionsHitByEditingPattern} submissions hit by the pattern.`}
            >
                <InputNumber />
            </Form.Item>
            {generateSimilarSubmissionsResult.isError && (
                <Alert message={generateSimilarSubmissionsResult?.error} type='error' showIcon closable />
            )}
            {errorEstimatingGenerateSimilarSubmissionsCost && (
                <Alert message={errorEstimatingGenerateSimilarSubmissionsCost} type='error' showIcon closable />
            )}
            <div className='d-flex-end'>
                <Form.Item className='mb-0'>
                    <Button type='primary' htmlType='submit' block loading={generateSimilarSubmissionsResult.isLoading}>
                        Generate {similarSubmissionsGenerationCost > 1 && `$${similarSubmissionsGenerationCost}`}
                    </Button>
                </Form.Item>
            </div>
        </Form>
    )
}

export default GenerateSimilarSubmissionsForm
